import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import AuthShell from "@/components/feature/AuthShell";
import { supabase } from "@/lib/supabase";
import { existeAdmin, verificarAccesoAdmin } from "@/lib/authAdmin";
import { getCallbackUrl } from "@/lib/authRedirect";
import { useAuth } from "@/context/AuthContext";
import BootstrapForm from "./components/BootstrapForm";
import LoginForm from "./components/LoginForm";
import CodeForm from "./components/CodeForm";
import RecoveryForm from "./components/RecoveryForm";

type Mode = "checking" | "bootstrap" | "login" | "signup_confirm" | "recovery" | "recovery_confirm";

function traducirError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login")) return "Correo o contraseña incorrectos.";
  if (lower.includes("already registered")) return "Este correo ya está registrado. Inicia sesión.";
  if (lower.includes("rate limit") || lower.includes("too many")) return "Demasiados intentos. Espera un momento.";
  if (lower.includes("password should be")) return "La contraseña debe tener al menos 8 caracteres.";
  if (lower.includes("security purposes")) return "Espera unos segundos antes de reintentar.";
  return message;
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const { session, isAdmin, loading: authLoading } = useAuth();

  const [mode, setMode] = useState<Mode>("checking");
  const [pendingEmail, setPendingEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  useEffect(() => {
    if (!authLoading && session && isAdmin) {
      navigate("/admin/panel", { replace: true });
    }
  }, [authLoading, session, isAdmin, navigate]);

  useEffect(() => {
    let active = true;
    existeAdmin()
      .then((exists) => {
        if (active) setMode(exists ? "login" : "bootstrap");
      })
      .catch(() => {
        if (active) setMode("login");
      });
    return () => {
      active = false;
    };
  }, []);

  // If a session already exists while no admin is registered yet, claim it automatically
  // (covers the email-confirmation link landing back on the login page).
  useEffect(() => {
    if (authLoading || !session || isAdmin || mode !== "bootstrap") return;
    let active = true;
    (async () => {
      const ok = await verificarAccesoAdmin(undefined, session.user.email ?? undefined);
      if (active && ok) navigate("/admin/panel", { replace: true });
    })();
    return () => {
      active = false;
    };
  }, [authLoading, session, isAdmin, mode, navigate]);

  const entrarComoAdmin = async (nombre?: string, email?: string): Promise<boolean> => {
    const ok = await verificarAccesoAdmin(nombre, email);
    if (!ok) {
      await supabase.auth.signOut();
      setError("Esta cuenta no tiene acceso de administrador.");
      return false;
    }
    navigate("/admin/panel", { replace: true });
    return true;
  };

  const resetMessages = () => {
    setError("");
    setResendMessage("");
  };

  const handleBootstrap = async ({ nombre, email, password }: { nombre: string; email: string; password: string }) => {
    resetMessages();
    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { nombre }, emailRedirectTo: getCallbackUrl() },
      });
      if (signUpError) {
        setError(traducirError(signUpError.message));
        return;
      }
      setPendingEmail(email);
      if (data.session) {
        await entrarComoAdmin(nombre, email);
      } else {
        setMode("signup_confirm");
      }
    } catch {
      setError("No pudimos crear la cuenta. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupCode = async (code: string) => {
    resetMessages();
    if (code.length !== 6) {
      setError("Ingresa los 6 dígitos del código.");
      return;
    }
    setLoading(true);
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: code,
        type: "signup",
      });
      if (verifyError) {
        setError(traducirError(verifyError.message));
        return;
      }
      if (data.session) {
        await entrarComoAdmin(undefined, pendingEmail);
      } else {
        setError("No pudimos confirmar la cuenta. Reenvía el código e inténtalo de nuevo.");
      }
    } catch {
      setError("No pudimos verificar el código.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async ({ email, password }: { email: string; password: string }) => {
    resetMessages();
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        if (/not confirmed|confirm/i.test(signInError.message)) {
          setPendingEmail(email);
          setError("Tu correo aún no está confirmado. Ingresa el código que te enviamos.");
          setMode("signup_confirm");
          return;
        }
        setError(traducirError(signInError.message));
        return;
      }
      if (data.session) {
        await entrarComoAdmin(undefined, data.session.user.email ?? undefined);
      }
    } catch {
      setError("No pudimos iniciar sesión. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendSignup = async () => {
    setResendMessage("");
    setError("");
    setResending(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email: pendingEmail,
        options: { emailRedirectTo: getCallbackUrl() },
      });
      if (resendError) {
        setError(traducirError(resendError.message));
      } else {
        setResendMessage("Te enviamos un código nuevo. Revisa tu correo.");
      }
    } finally {
      setResending(false);
    }
  };

  const handleRecovery = async (email: string) => {
    resetMessages();
    setLoading(true);
    try {
      const { error: recoveryError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getCallbackUrl("/auth/reset-password"),
      });
      if (recoveryError) {
        setError(traducirError(recoveryError.message));
        return;
      }
      setPendingEmail(email);
      setMode("recovery_confirm");
    } catch {
      setError("No pudimos enviar el código de recuperación.");
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryCode = async (code: string) => {
    resetMessages();
    if (code.length !== 6) {
      setError("Ingresa los 6 dígitos del código.");
      return;
    }
    setLoading(true);
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: code,
        type: "recovery",
      });
      if (verifyError) {
        setError(traducirError(verifyError.message));
        return;
      }
      if (data.session) {
        navigate("/auth/reset-password", { replace: true });
      } else {
        setError("No pudimos verificar el código. Reenvíalo e inténtalo de nuevo.");
      }
    } catch {
      setError("No pudimos verificar el código.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendRecovery = async () => {
    setResendMessage("");
    setError("");
    setResending(true);
    try {
      const { error: resendError } = await supabase.auth.resetPasswordForEmail(pendingEmail, {
        redirectTo: getCallbackUrl("/auth/reset-password"),
      });
      if (resendError) {
        setError(traducirError(resendError.message));
      } else {
        setResendMessage("Te enviamos un código nuevo. Revisa tu correo.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell>
      <Seo
        title="Acceso administrativo"
        path="/admin/login"
        robots="noindex, nofollow, noarchive"
      />
      {mode === "checking" && (
        <div className="flex flex-col items-center gap-3 py-10">
          <i className="ri-loader-4-line animate-spin text-3xl leading-none text-primary-500" aria-hidden="true"></i>
          <p className="text-sm text-foreground-600">Preparando el acceso…</p>
        </div>
      )}

      {mode === "bootstrap" && (
        <BootstrapForm loading={loading} error={error} onSubmit={handleBootstrap} />
      )}

      {mode === "login" && (
        <LoginForm
          loading={loading}
          error={error}
          onSubmit={handleLogin}
          onForgot={() => {
            resetMessages();
            setMode("recovery");
          }}
        />
      )}

      {mode === "signup_confirm" && (
        <CodeForm
          title="Confirma tu cuenta"
          description={
            <>
              Enviamos un código de 6 dígitos a <strong className="text-foreground-900">{pendingEmail}</strong>.
              Ingrésalo para activar tu cuenta de administrador.
            </>
          }
          loading={loading}
          error={error}
          resending={resending}
          resendMessage={resendMessage}
          onSubmit={handleSignupCode}
          onResend={handleResendSignup}
          onBack={() => {
            resetMessages();
            setMode("login");
          }}
          submitLabel="Confirmar cuenta"
        />
      )}

      {mode === "recovery" && (
        <RecoveryForm
          loading={loading}
          error={error}
          onSubmit={handleRecovery}
          onBack={() => {
            resetMessages();
            setMode("login");
          }}
        />
      )}

      {mode === "recovery_confirm" && (
        <CodeForm
          title="Restablecer contraseña"
          description={
            <>
              Enviamos un código de recuperación a <strong className="text-foreground-900">{pendingEmail}</strong>.
              Ingrésalo para crear una contraseña nueva.
            </>
          }
          loading={loading}
          error={error}
          resending={resending}
          resendMessage={resendMessage}
          onSubmit={handleRecoveryCode}
          onResend={handleResendRecovery}
          onBack={() => {
            resetMessages();
            setMode("login");
          }}
          submitLabel="Verificar y continuar"
        />
      )}
    </AuthShell>
  );
}